import React, { useState, useEffect } from "react";
import { Card, List, Empty, Modal, message, Typography } from "antd";
import { EnvironmentOutlined, RightOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import Button from "../../../components/common/button";
import TopDealsForm from "./form";
import DeleteMemberModal from "../../../components/common/modalMember";
import { getTopDealsCities, getTopDealsByCity, deleteTopDealsByCity } from "../../../api/topDeals";
import colors from "../../../share/constants/constants";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { saveTopDeals } from "../../../api/topDeals";
import { MenuOutlined } from "@ant-design/icons";

const SortableDealItem = ({ id, deal, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-3 mb-2 bg-white border border-inputFieldGrey rounded-xl"
    >
      <div className="flex items-center flex-1">
        <div
          {...attributes}
          {...listeners}
          className="cursor-move mr-3 text-gray-500 hover:text-primaryPurple"
        >
          <MenuOutlined />
        </div>
        <div className="flex-1">
          <div className="font-medium text-black">
            Rank {deal.rank}: {deal.vendorName || deal.vendorId?.businessName || "Unknown Vendor"}
          </div>
          <div className="text-sm text-gray-600">
            {deal.serviceName || deal.serviceId?.serviceName || "Unknown Service"}
          </div>
        </div>
      </div>
      {onRemove && (
        <button
          onClick={() => onRemove(deal.rank)}
          className="ml-3 text-red-500 hover:text-red-700"
        >
          <DeleteOutlined />
        </button>
      )}
    </div>
  );
};

const TopDealsManager = () => {
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [cityDeals, setCityDeals] = useState([]);
  const [cityData, setCityData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [cityToDelete, setCityToDelete] = useState(null);
  const [editDeals, setEditDeals] = useState([]);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    setLoading(true);
    try {
      const res = await getTopDealsCities();
      if (res) {
        setCities(res.cities || res || []);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCityClick = async (city) => {
    setSelectedCity(city);
    setDealsLoading(true);
    try {
      const res = await getTopDealsByCity(city);
      if (res) {
        const deals = res.deals || res || [];
        const sortedDeals = deals.sort((a, b) => a.rank - b.rank);
        setCityDeals(sortedDeals);
        setEditDeals(sortedDeals.map(deal => ({
          rank: deal.rank,
          vendorId: deal.vendorId?._id || deal.vendorId,
          vendorName: deal.vendorId?.businessName || deal.vendorName || "",
          serviceId: deal.serviceId?._id || deal.serviceId,
          serviceName: deal.serviceId?.serviceName || deal.serviceName || "",
        })));
        if (res.cityData) {
          setCityData(res.cityData);
        } else {
          setCityData({ city, deals: sortedDeals });
        }
      }
    } catch (error) {
      console.error("Error fetching deals:", error);
      setCityDeals([]);
      setEditDeals([]);
    } finally {
      setDealsLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedCity(null);
    setCityDeals([]);
    setCityData(null);
    setIsEditMode(false);
    setEditDeals([]);
  };

  const handleModalSuccess = () => {
    fetchCities();
    if (selectedCity) {
      handleCityClick(selectedCity);
    }
  };

  const handleDeleteCity = (city, e) => {
    e.stopPropagation();
    setCityToDelete(city);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!cityToDelete) return;
    try {
      const res = await deleteTopDealsByCity(cityToDelete);
      if (res) {
        message.success("City deals deleted successfully");
        setIsDeleteModalOpen(false);
        setCityToDelete(null);
        if (selectedCity === cityToDelete) {
          handleBack();
        }
        fetchCities();
      }
    } catch (error) {
      message.error("Failed to delete city deals");
    }
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditDeals(cityDeals.map(deal => ({
      rank: deal.rank,
      vendorId: deal.vendorId?._id || deal.vendorId,
      vendorName: deal.vendorId?.businessName || deal.vendorName || "",
      serviceId: deal.serviceId?._id || deal.serviceId,
      serviceName: deal.serviceId?.serviceName || deal.serviceName || "",
    })));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = editDeals.findIndex((deal) => deal.rank === active.id);
      const newIndex = editDeals.findIndex((deal) => deal.rank === over.id);

      const reorderedDeals = arrayMove(editDeals, oldIndex, newIndex).map(
        (deal, index) => ({
          ...deal,
          rank: index + 1,
        })
      );

      setEditDeals(reorderedDeals);
    }
  };

  const handleRemoveDeal = (rank) => {
    const updatedDeals = editDeals
      .filter((deal) => deal.rank !== rank)
      .map((deal, index) => ({
        ...deal,
        rank: index + 1,
      }));
    setEditDeals(updatedDeals);
  };

  const handleSaveEdit = async () => {
    if (editDeals.length === 0) {
      message.warning("At least one deal is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        city: selectedCity,
        latitude: cityData?.coordinates?.lat || cityData?.latitude,
        longitude: cityData?.coordinates?.lng || cityData?.longitude,
        deals: editDeals.map((deal) => ({
          rank: deal.rank,
          vendorId: deal.vendorId,
          serviceId: deal.serviceId,
        })),
      };

      const res = await saveTopDeals(payload);
      if (res) {
        message.success("Deals updated successfully");
        setIsEditMode(false);
        handleCityClick(selectedCity);
      }
    } catch (error) {
      message.error("Failed to update deals");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-2 space-x-2 w-full pr-2">
      <div className="flex justify-between items-center ml-2 mt-2">
        <span className="text-lg font-medium text-black ml-1">
          {selectedCity ? `${selectedCity} - Top Deals` : "Top Deals Manager"}
        </span>
        <div className="flex gap-2">
          {!selectedCity && (
            <Button
              title="Add New City Deals"
              color={colors.primaryPurple}
              className="w-auto px-4"
              onClick={() => setModalOpen(true)}
            />
          )}
          {selectedCity && !isEditMode && (
            <>
              <Button
                title="Edit"
                color={colors.primaryPurple}
                className="w-auto px-4"
                onClick={handleEditClick}
              />
              <Button
                title="Back"
                color={colors.primaryPurple}
                className="w-auto px-4"
                onClick={handleBack}
              />
            </>
          )}
          {selectedCity && isEditMode && (
            <>
              <Button
                title="Save"
                color={colors.primaryPurple}
                className="w-auto px-4"
                onClick={handleSaveEdit}
                loading={saving}
              />
              <Button
                title="Cancel"
                color={colors.primaryPurple}
                className="w-auto px-4"
                onClick={handleCancelEdit}
              />
            </>
          )}
        </div>
      </div>
      <div>
        <Card className="rounded-2xl bg-white xl:relative">
          <div className="border-solid border-inputFieldGrey rounded-sm p-2 lg:px-5">
            {!selectedCity ? (
              <List
                loading={loading}
                dataSource={cities}
                locale={{ emptyText: "No cities found" }}
                renderItem={(city) => (
                  <List.Item
                    className="cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
                    onClick={() => handleCityClick(city)}
                    actions={[
                      <button
                        key="delete"
                        onClick={(e) => handleDeleteCity(city, e)}
                        className="text-red-500 hover:text-red-700 p-2"
                      >
                        <DeleteOutlined />
                      </button>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<EnvironmentOutlined className="text-primaryPurple text-xl" />}
                      title={
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-medium">{city}</span>
                          <RightOutlined className="text-gray-400" />
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <div>
                {dealsLoading ? (
                  <div className="text-center py-8">Loading deals...</div>
                ) : isEditMode ? (
                  editDeals.length > 0 ? (
                    <div className="mt-4">
                      <Typography.Title level={5} className="mt-0 mb-2">
                        Top Deals (Drag to reorder)
                      </Typography.Title>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={editDeals.map((deal) => deal.rank)}
                          strategy={verticalListSortingStrategy}
                        >
                          {editDeals.map((deal) => (
                            <SortableDealItem
                              key={deal.rank}
                              id={deal.rank}
                              deal={deal}
                              onRemove={handleRemoveDeal}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    </div>
                  ) : (
                    <Empty
                      description="No deals to edit"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )
                ) : cityDeals.length > 0 ? (
                  <List
                    dataSource={cityDeals}
                    renderItem={(deal, index) => (
                      <List.Item className="border-b border-inputFieldGrey py-4">
                        <div className="w-full">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="w-8 h-8 rounded-full bg-primaryPurple text-white flex items-center justify-center font-bold mr-3">
                                {deal.rank || index + 1}
                              </div>
                              <div>
                                <div className="font-semibold text-lg">
                                  {deal.vendorName || deal.vendorId?.businessName || "Unknown Vendor"}
                                </div>
                                <div className="text-gray-600">
                                  {deal.serviceName || deal.serviceId?.serviceName || "Unknown Service"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Empty
                    description="No deals found for this city"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </div>
            )}
          </div>
        </Card>
      </div>

      <TopDealsForm
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onSuccess={handleModalSuccess}
        isEdit={false}
      />

      {isDeleteModalOpen && (
        <DeleteMemberModal
          title="Delete City Deals"
          text={`Are you sure you want to delete all deals for ${cityToDelete}?`}
          onCancel={() => {
            setIsDeleteModalOpen(false);
            setCityToDelete(null);
          }}
          onOk={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default TopDealsManager;
